/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
} from "react";

import { usePlayerCurrentMedia } from "..";
import { usePlayerFeatures } from "../../BasePlayer";
import { useCustomPlayerContext } from "../../CustomPlayerContext";

import { useStartPositionLoader } from "./useStartPositionLoader";

type PlayerContinueWatchingProviderProps = {
  onLoaded: (startPosition: number, autoPlay: boolean) => void;
  children: ReactNode;
};

type PlayerContinueWatchingContextValue = {
  showOverlay: boolean;
  startPosition: number;
};

const PlayerContinueWatchingContext =
  createContext<PlayerContinueWatchingContextValue>(
    {} as PlayerContinueWatchingContextValue,
  );

export const PlayerContinueWatchingProvider = (
  props: PlayerContinueWatchingProviderProps,
) => {
  const { onLoaded, children } = props;

  const { continueWatching: continueWatchingFeatureEnabled, autoPlay } =
    usePlayerFeatures();
  const { streamType, playInfo, media } = usePlayerCurrentMedia();

  const { skipContinueWatching = false } = useCustomPlayerContext();

  const continueWatchingEnabled =
    !skipContinueWatching && continueWatchingFeatureEnabled;

  const { startPosition, loaded } = useStartPositionLoader({
    playInfo,
    streamType,
    enabled: continueWatchingEnabled,
  });

  const durationInSeconds = (playInfo?.Duration ?? media?.Duration ?? 0) / 1000;
  const isNearEnd =
    durationInSeconds > 0 &&
    startPosition >= durationInSeconds - Math.min(5, durationInSeconds * 0.05);
  const showOverlay =
    continueWatchingEnabled && loaded && startPosition > 0 && !isNearEnd; //Show overlay only if there is start position and it is not within 5s or 5% of the end

  const calledOnLoadedRef = useRef<boolean>(false);

  useEffect(() => {
    if (loaded) {
      const enableAutoPlay = showOverlay ? false : autoPlay;

      // make sure to call onLoaded only once since it may initialize
      // player internal components multiple times
      if (!calledOnLoadedRef.current) {
        calledOnLoadedRef.current = true;
        // When overlay is shown, start video at 0 so the user can choose
        // via the popup — "Resume" will seek to startPosition separately.
        // When no overlay, pass startPosition directly so video starts there.
        onLoaded(showOverlay ? 0 : startPosition, enableAutoPlay);
      }
    }
  }, [autoPlay, loaded, onLoaded, showOverlay, startPosition]);

  if (!loaded) {
    return <></>;
  }

  return (
    <PlayerContinueWatchingContext.Provider
      value={{ startPosition, showOverlay }}
    >
      {children}
    </PlayerContinueWatchingContext.Provider>
  );
};

export const usePlayerContinueWatching =
  (): PlayerContinueWatchingContextValue => {
    const context = useContext(PlayerContinueWatchingContext);

    if (!context) {
      throw new Error("Component beyond PlayerContinueWatchingContext");
    }

    return context;
  };
